import { useState, useEffect } from 'react'
import { ChevronRight, ChevronLeft, X, Sparkles } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

type TourStep = {
  title: string
  description: string
  action?: { label: string; to: string }
}

const TOUR_STEPS: TourStep[] = [
  {
    title: 'Welcome to Palstro!',
    description: 'Your ERP is ready. This quick tour (5 steps) shows you the most important features. You can skip at any time.',
  },
  {
    title: 'Dashboard',
    description: 'Your dashboard shows today\'s sales, pending orders, low stock alerts and outstanding receivables — all in one view.',
    action: { label: 'Go to Dashboard', to: '/dashboard' },
  },
  {
    title: 'Point of Sale',
    description: 'Tap products to add to cart, select a customer, collect payment — done. Receipts are printed automatically.',
    action: { label: 'Open POS', to: '/pos' },
  },
  {
    title: 'Settings',
    description: 'Set up your team (invite staff), configure taxes, payment accounts, and module permissions. All customisable.',
    action: { label: 'Open Settings', to: '/settings/business' },
  },
  {
    title: 'You\'re all set!',
    description: 'The blue ? button (bottom right) opens context-sensitive help on any page. You can re-take this tour from Settings → Business.',
  },
]

const TOUR_KEY = 'palstro-tour-done'

interface Props {
  onDone: () => void
}

export function WelcomeTour({ onDone }: Props) {
  const [step, setStep] = useState(0)
  const navigate = useNavigate()

  const current = TOUR_STEPS[step]
  const isLast = step === TOUR_STEPS.length - 1
  const isFirst = step === 0

  function next() {
    if (isLast) {
      localStorage.setItem(TOUR_KEY, '1')
      onDone()
    } else {
      setStep(s => s + 1)
    }
  }

  function skip() {
    localStorage.setItem(TOUR_KEY, '1')
    onDone()
  }

  function goAndNext() {
    if (current.action) navigate(current.action.to)
    next()
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />

      {/* Tour card */}
      <div className="fixed inset-x-4 bottom-24 z-50 lg:inset-auto lg:bottom-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:w-[420px]">
        <div className="bg-white rounded-2xl shadow-2xl p-6">
          {/* Progress dots */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-1.5">
              {TOUR_STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === step ? 'w-6 bg-brand-500' : i < step ? 'w-1.5 bg-brand-200' : 'w-1.5 bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <button onClick={skip} className="text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Icon */}
          <div className="h-10 w-10 rounded-2xl bg-brand-50 flex items-center justify-center mb-3">
            <Sparkles className="h-5 w-5 text-brand-500" />
          </div>

          {/* Content */}
          <h3 className="font-bold text-gray-900 text-lg mb-2">{current.title}</h3>
          <p className="text-sm text-gray-600 leading-relaxed mb-5">{current.description}</p>

          {/* Actions */}
          <div className="flex gap-3">
            {!isFirst && (
              <button className="btn-secondary" onClick={() => setStep(s => s - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}

            {current.action && (
              <button className="btn-secondary flex-1" onClick={goAndNext}>
                {current.action.label}
              </button>
            )}

            <button className="btn-primary flex-1" onClick={isLast ? skip : next}>
              {isLast ? 'Get Started!' : (
                <>Next <ChevronRight className="h-4 w-4" /></>
              )}
            </button>
          </div>

          <p className="text-center text-xs text-gray-400 mt-3">
            Step {step + 1} of {TOUR_STEPS.length}
          </p>
        </div>
      </div>
    </>
  )
}

export function useShouldShowTour() {
  const [show, setShow] = useState(false)

  useEffect(() => {
    // Show tour only if never shown before
    const done = localStorage.getItem(TOUR_KEY)
    if (!done) {
      // Small delay so the app has time to render first
      const t = setTimeout(() => setShow(true), 1200)
      return () => clearTimeout(t)
    }
  }, [])

  return { showTour: show, dismissTour: () => setShow(false) }
}
